import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { SingleBetItemState } from 'src/domains/sportsbook/betting/betSlipState/betsState/SingleBetItemState';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { BetsFilter } from 'src/domains/sportsbook/betting/state/BetsState';
import {
    CashOutSuccessMessage,
    CashOutThumbsUpIconWrapper,
    CashOutBtnWrapper,
} from 'src/domains/sportsbook/betting/bettingTab/cashout/selection/selectionNew/CashOutButtons.style';
import { useAppStateContext } from 'src/appState/AppState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

interface PropsType {
    betItem: SingleBetItemState;
}

export const CashOutButtons = observer('CashOutButtons', ({ betItem }: PropsType) => {
    const { appSportsBookState } = useAppStateContext();
    const {
        betsState: { selectedFilter },
    } = appSportsBookState;
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const {
        hasCashoutEnabled,
        componentVariants: { betslipVariant },
    } = configComponents.config;

    const buttonSize = betslipVariant === DefaultComponentVariantType.SECONDARY ? 'small' : 'large';

    const { cashOut, cashOutItem, isLoadingCashOut, isShowSuccessCashOut } = betItem;
    if (selectedFilter === BetsFilter.CASH_OUT) {
        if (isShowSuccessCashOut) {
            return (
                <CashOutSuccessMessage
                    className='bet-selection__message'
                    data-test='cash-out-success-message'
                    variant={betslipVariant}
                >
                    <CashOutThumbsUpIconWrapper position='up' />
                    <I18n
                        langKey='bets.selection.cash-out-successful.label'
                        defaultText='Cash Out Successful!'
                    />
                </CashOutSuccessMessage>
            );
        }

        if (cashOutItem === null) {
            return null;
        }

        const isCashoutSuspended = !hasCashoutEnabled || !cashOutItem.enabled;

        if (isCashoutSuspended) {
            return (
                <CashOutBtnWrapper
                    disabled={true}
                    size={buttonSize}
                    data-test='cash-out-button'
                    variant={betslipVariant}
                >
                    <I18n
                        langKey='bets.selection.cash-out.suspended'
                        defaultText='Cash Out Suspended'
                    />
                </CashOutBtnWrapper>
            );
        } else {
            const { onConfirmCashOut, onRetrieveCashOut, isConfirm } = betItem;
            const amount = basicDataModel.money(
                configComponents.precision.newFromAnything(betItem.cashOutItem?.value ?? '')
            );
            const isCashoutExist = !cashOut;

            if (isCashoutExist) {
                const isDisabled = isLoadingCashOut || cashOutItem.value === 0;
                const dataTest = isConfirm ? 'confirm-cash-out-button' : 'cash-out-button';
                return (
                    <CashOutBtnWrapper
                        dataTest={dataTest}
                        isLoading={isLoadingCashOut}
                        onClick={isConfirm ? onRetrieveCashOut : onConfirmCashOut}
                        disabled={isDisabled}
                        size={buttonSize}
                        variant={betslipVariant}
                    >
                        {isConfirm ? (
                            <I18n
                                langKey='bets.selection.cash-out.confirm'
                                defaultText='Confirm {amount} cash out'
                                params={{ amount }}
                            />
                        ) : (
                            <I18n
                                langKey='bets.selection.cash-out.button'
                                defaultText='Cash Out {amount}'
                                params={{ amount }}
                            />
                        )}
                    </CashOutBtnWrapper>
                );
            }
            return null;
        }
    }
    return null;
});
